import React, { useEffect, useRef, useState } from 'react';
import { formatPhone, inp, lbl, noAutofill, row2, row3 } from './shared';

const STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

async function searchAddress(query) {
  const res = await fetch(`/api/address-search?q=${encodeURIComponent(query)}`);
  if (res.ok) return res.json();

  if (!['localhost', '127.0.0.1'].includes(window.location.hostname)) return [];

  const fallback = await fetch(
    `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&addressdetails=1&limit=6&countrycodes=us`,
    { headers: { 'Accept-Language': 'en-US' } }
  );
  if (!fallback.ok) return [];
  const data = await fallback.json();
  const stateAbbr = {
    Alabama:'AL', Alaska:'AK', Arizona:'AZ', Arkansas:'AR', California:'CA',
    Colorado:'CO', Connecticut:'CT', Delaware:'DE', Florida:'FL', Georgia:'GA',
    Hawaii:'HI', Idaho:'ID', Illinois:'IL', Indiana:'IN', Iowa:'IA',
    Kansas:'KS', Kentucky:'KY', Louisiana:'LA', Maine:'ME', Maryland:'MD',
    Massachusetts:'MA', Michigan:'MI', Minnesota:'MN', Mississippi:'MS',
    Missouri:'MO', Montana:'MT', Nebraska:'NE', Nevada:'NV', 'New Hampshire':'NH',
    'New Jersey':'NJ', 'New Mexico':'NM', 'New York':'NY', 'North Carolina':'NC',
    'North Dakota':'ND', Ohio:'OH', Oklahoma:'OK', Oregon:'OR', Pennsylvania:'PA',
    'Rhode Island':'RI', 'South Carolina':'SC', 'South Dakota':'SD', Tennessee:'TN',
    Texas:'TX', Utah:'UT', Vermont:'VT', Virginia:'VA', Washington:'WA',
    'West Virginia':'WV', Wisconsin:'WI', Wyoming:'WY',
  };
  return data.map(result => {
    const address = result.address || {};
    let houseNumber = address.house_number || '';
    if (!houseNumber) {
      const match = query.trim().match(/^(\d+[-\d]*\s*[A-Za-z]?)\s/);
      if (match) houseNumber = match[1].trim();
    }
    return {
      street: [houseNumber, address.road].filter(Boolean).join(' '),
      city: address.city || address.town || address.village || address.hamlet || '',
      county: address.county || '',
      state: stateAbbr[address.state] || address.state || '',
      zip: address.postcode ? address.postcode.split('-')[0] : '',
      display: result.display_name,
    };
  });
}

function AddressInput({ value, onChange }) {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  useEffect(() => setQuery(value || ''), [value]);

  function type(next) {
    setQuery(next);
    onChange('address', next);
    clearTimeout(timer.current);
    if (next.length < 4) {
      setSuggestions([]);
      return;
    }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        setSuggestions(await searchAddress(next));
      } finally {
        setLoading(false);
      }
    }, 350);
  }

  function choose(item) {
    setSuggestions([]);
    const street = item.street || item.display || '';
    setQuery(street);
    onChange('address', street);
    if (item.city) onChange('city', item.city);
    if (item.county) onChange('county', item.county);
    if (item.state) onChange('state', item.state);
    if (item.zip) onChange('zip', item.zip);
  }

  return (
    <div style={{ position: 'relative', marginBottom: 18 }}>
      <label style={lbl}>
        Address
        {loading && <span style={{ color: '#7a99b8', marginLeft: 8, fontWeight: 400 }}>Searching...</span>}
      </label>
      <input style={inp} value={query} onChange={e => type(e.target.value)} {...noAutofill('physical-address')} />
      {suggestions.length > 0 && (
        <div style={suggestionBox}>
          {suggestions.map((item, i) => (
            <button key={i} type="button" style={suggestion} onMouseDown={() => choose(item)}>
              <strong>{item.street || String(item.display || '').split(',')[0]}</strong>
              <span>{[item.city, item.state, item.zip].filter(Boolean).join(', ')}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function BasicInfo({ data, onChange }) {
  return (
    <div>
      <div style={row3}>
        <div>
          <label style={lbl}>First Name</label>
          <input style={inp} value={data.firstName} onChange={e => onChange('firstName', e.target.value)} {...noAutofill('first-name')} />
        </div>
        <div>
          <label style={lbl}>Middle Name</label>
          <input style={inp} value={data.middleName} onChange={e => onChange('middleName', e.target.value)} {...noAutofill('middle-name')} />
        </div>
        <div>
          <label style={lbl}>Last Name</label>
          <input style={inp} value={data.lastName} onChange={e => onChange('lastName', e.target.value)} {...noAutofill('last-name')} />
        </div>
      </div>

      <div style={row2}>
        <div>
          <label style={lbl}>Email</label>
          <input style={inp} type="email" value={data.email} onChange={e => onChange('email', e.target.value)} {...noAutofill('email')} />
        </div>
        <div>
          <label style={lbl}>Phone Number</label>
          <input
            style={inp}
            type="tel"
            placeholder="(555) 000-0000"
            value={data.phone}
            onChange={e => onChange('phone', formatPhone(e.target.value))}
            {...noAutofill('phone')}
          />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 14, marginBottom: 18 }}>
        <AddressInput value={data.address} onChange={onChange} />
        <div>
          <label style={lbl}>Ste/Apt</label>
          <input style={inp} value={data.address2} onChange={e => onChange('address2', e.target.value)} {...noAutofill('address-2')} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 14, marginBottom: 18 }}>
        <div>
          <label style={lbl}>City</label>
          <input style={inp} value={data.city} onChange={e => onChange('city', e.target.value)} {...noAutofill('city')} />
        </div>
        <div>
          <label style={lbl}>State</label>
          <select style={inp} value={data.state} onChange={e => onChange('state', e.target.value)}>
            <option value="">--</option>
            {STATES.map(st => <option key={st}>{st}</option>)}
          </select>
        </div>
        <div>
          <label style={lbl}>ZIP</label>
            <input style={inp} value={data.zip} onChange={e => onChange('zip', e.target.value)} {...noAutofill('zip')} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
        <div>
          <label style={lbl}>County</label>
          <input style={inp} value={data.county} onChange={e => onChange('county', e.target.value)} {...noAutofill('county')} />
        </div>
      </div>
    </div>
  );
}

const suggestionBox = { position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20, background: '#fff', border: '1.5px solid #c5d5e8', borderRadius: 8, boxShadow: '0 8px 18px rgba(26,51,100,0.14)', overflow: 'hidden' };
const suggestion = { display: 'grid', gap: 2, width: '100%', textAlign: 'left', border: 0, background: '#fff', padding: '10px 12px', color: '#1a3364', cursor: 'pointer' };
